import React, { Component, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console for debugging

    // In production, you would send this to an error reporting service
    // Example: Sentry, LogRocket, Bugsnag, etc.
    // reportError(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='min-h-screen flex items-center justify-center bg-background p-4'>
          <div className='max-w-md mx-auto text-center space-y-6'>
            {/* Error Icon */}
            <div className='w-20 h-20 mx-auto bg-destructive/10 rounded-full flex items-center justify-center'>
              <AlertCircle className='w-10 h-10 text-destructive' />
            </div>

            {/* Error Content */}
            <div className='space-y-4'>
              <h1 className='text-2xl font-bold text-foreground'>Something went wrong</h1>
              <p className='text-muted-foreground'>
                We&apos;re sorry, but something unexpected happened. Our team has been notified.
              </p>

              {/* Error Details (only in development) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className='text-left bg-muted/50 p-4 rounded-lg text-sm'>
                  <summary className='cursor-pointer font-medium'>Error Details</summary>
                  <pre className='mt-2 whitespace-pre-wrap text-xs text-destructive'>
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}
            </div>

            {/* Actions */}
            <div className='flex flex-col sm:flex-row gap-3 justify-center'>
              <Button
                onClick={this.handleReset}
                variant='outline'
                className='flex items-center gap-2'
              >
                <RefreshCw className='w-4 h-4' />
                Try Again
              </Button>
              <Button onClick={this.handleGoHome} className='flex items-center gap-2'>
                <Home className='w-4 h-4' />
                Go to Dashboard
              </Button>
            </div>

            {/* Error ID for support */}
            <div className='text-xs text-muted-foreground'>Error ID: {Date.now().toString(36)}</div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
